import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IDataSourceItem,
  IDataSourceState,
  ITableColumnData,
  dataSourceTablesPaginationInitialState,
  datasourcesInitialState,
  initialDeleteDataSourceProps,
  studioInitialPath,
  tableColumnsInitialState,
} from './initialState';
import { allCSVDataSourceTypes } from '../components/Datasets/IngestSettings/components/IncrementalIngestionSettings/utilities/IncrementalIngestionsSettings.constants';
import { applyFilters } from '../utilities/applyDataSourcesFilters';
import { calculatePaginationCount } from './datasources.constants';
import { IDatasourceList } from '../Datasources.interfaces';
import {
  TABLE_COL_COUNT_LOCAL_SETTING,
  TABLE_COL_PAGE_COUNT_DEFAULT,
} from '../components/DataSetSettings/ColumnListTable/ColumnList/ColumnList.constants';

export const updateDatasourcesReduxSlice = {
  setIsLoadingDatasources: (state: IDataSourceState, action: PayloadAction<boolean>) => {
    state.isLoading = action.payload;
    state.hasLoadingError = false;
  },

  setIsDataSourceTypesLoading: (state: IDataSourceState, action: PayloadAction<boolean>) => {
    state.types.isLoading = action.payload;
  },

  setIsDataSourceTypesError: (state: IDataSourceState, action: PayloadAction<boolean>) => {
    state.types.hasError = action.payload;
  },

  setErrorLoadingDataSources: (
    state: IDataSourceState,
    action: PayloadAction<{
      hasLoadingError: boolean;
      errorCode: number;
    }>
  ) => {
    state.isLoading = false;
    state.hasLoadingError = action.payload.hasLoadingError;
    state.errorCode = action.payload.errorCode;
  },

  setDataSources: (state: IDataSourceState, action: PayloadAction<IDatasourceList>) => {
    state.isLoading = false;
    const datasourceList: IDatasourceList = (
      Object.keys(action.payload) as Array<keyof IDatasourceList>
    ).reduce((acc, type) => {
      return {
        ...acc,
        [type]: action.payload[type].map((ds: IDataSourceItem) => ({
          ...ds,
          // init empty object - later used in selectedDatasource
          userName: '',
          password: '',
          connectionUrl: '',
          props: {},
        })),
      };
    }, {} as IDatasourceList);

    state.datasourceList = datasourceList;
    state.filteredList = datasourceList;
    state.isDataSourcesEmpty = Object.keys(datasourceList).length === 0;
  },

  setDataSourceTypes: (
    state: IDataSourceState,
    action: PayloadAction<{
      dataSourceTypesList: any[];
    }>
  ) => {
    state.types.isLoading = false;
    state.types.typesList = action.payload.dataSourceTypesList;
    state.types.csvTypeIds = action.payload.dataSourceTypesList
      .filter((type: any) => allCSVDataSourceTypes.includes(type.id))
      .map((item: any) => item.id);
    state.types.typesMap = action.payload.dataSourceTypesList.reduce(
      (acc, curr) => ({ ...acc, [curr.id]: curr }),
      {}
    );
  },

  resetDataSourceTablesPagination: (state: IDataSourceState) => {
    state.tableInfo.tablesPagination = dataSourceTablesPaginationInitialState;
  },

  filterDataSourcesByName: (state: IDataSourceState, action: PayloadAction<string>) => {
    const searchTerm = action.payload.toLowerCase();
    state.filterValues.searchTerm = searchTerm;
    // Apply both filters - type and search
    state.filteredList = applyFilters({
      datasources: state.datasourceList,
      searchTerm: searchTerm,
      filterTypeId: state.filterValues.typeId,
    });
  },

  filterDataSourcesByType: (state: IDataSourceState, action: PayloadAction<number>) => {
    const typeId = action.payload;
    state.filterValues.typeId = typeId;
    // Apply both filters - type and search
    state.filteredList = applyFilters({
      datasources: state.datasourceList,
      searchTerm: state.filterValues.searchTerm,
      filterTypeId: typeId,
    });
  },

  setTablesIsLoading: (state: IDataSourceState, action: PayloadAction<boolean>) => {
    state.tableInfo.isLoading = action.payload;
    state.tableInfo.hasError = false;
  },

  setErrorLoadingTables: (state: IDataSourceState, action: PayloadAction<boolean>) => {
    state.tableInfo.hasError = action.payload;
    // if there is an error, set loading to false
    if (action.payload) {
      state.tableInfo.isLoading = false;
    }
  },

  setDataSourceTables: (
    state: IDataSourceState,
    action: PayloadAction<{
      [key: string]: any | any[];
    }>
  ) => {
    let newActiveDataSourceTotalCols = state.activeDataSourceTotalCols;
    let newActiveDataSourceSelectedColCount = state.activeDataSourceSelectedColCount;

    const indexedTables = action.payload.data.reduce(
      (dataSourceTables: { [key: string]: any }, dataSourceTable: any) => {
        if (dataSourceTable.tableName === state.tableInfo.activeTableName) {
          newActiveDataSourceTotalCols = dataSourceTable.total;
          newActiveDataSourceSelectedColCount = dataSourceTable.selectedCount;
        }
        return {
          ...dataSourceTables,
          [dataSourceTable.tableName]: dataSourceTable,
        };
      },
      {}
    );

    state.tableInfo.isLoading = false;
    state.tableInfo.tablesRaw = action.payload.data;
    state.tableInfo.tablesIndexedList = indexedTables;
    state.activeDataSourceTotalCols = newActiveDataSourceTotalCols;
    state.tableInfo.totalTables = action.payload.totalCount as unknown as number;
    state.activeDataSourceSelectedColCount = newActiveDataSourceSelectedColCount;
  },

  setActiveDataSourceTable: (
    state: IDataSourceState,
    action: PayloadAction<{ tableName: string | null; resetErrors: boolean }>
  ) => {
    state.tableInfo.activeTableName = action.payload.tableName;
    if (action.payload.resetErrors) {
      // reset state
      state.tableInfo.isLoading = false;
      state.tableInfo.hasError = false;
    }
  },

  setActiveDataSourceTableColumnCounts: (
    state: IDataSourceState,
    action: PayloadAction<{
      total: number;
      selectedCount: number;
    }>
  ) => {
    state.activeDataSourceTotalCols = action.payload.total;
    state.activeDataSourceSelectedColCount = action.payload.selectedCount;
  },

  updateDataSourceTablesPaginationCurrentPage: (
    state: IDataSourceState,
    action: PayloadAction<number>
  ) => {
    state.tableInfo.tablesPagination.currentPage = action.payload;
  },

  updateDataSourceTablesPaginationPageLimit: (
    state: IDataSourceState,
    action: PayloadAction<number>
  ) => {
    state.tableInfo.tablesPagination.pageLimit = action.payload;
  },

  updateDataSourceTablesPaginationSearchTerm: (
    state: IDataSourceState,
    action: PayloadAction<string>
  ) => {
    state.tableInfo.tablesPagination.searchTerm = action.payload;
  },

  initLoadingColumns: (
    state: IDataSourceState,
    action: PayloadAction<{ isInitialLoad?: boolean }>
  ) => {
    // reset to initial
    state.tableColumns.columnInfo.data = tableColumnsInitialState.columnInfo.data;
    state.tableColumns.columnInfo.searchCount = tableColumnsInitialState.columnInfo.searchCount;
    state.tableColumns.columnList = tableColumnsInitialState.columnList;
    state.activeDataSourceSelectedColCount = 0;
    state.tableColumns.isLoading = true;
    state.tableColumns.hasError = false;
    state.tableColumns.errorCode = -1;

    // on initial load, reset to the default state for table pagination & searchStr
    if (action.payload.isInitialLoad) {
      state.tableColumns.columnInfo.colCountPerPage = Number(
        localStorage?.getItem(TABLE_COL_COUNT_LOCAL_SETTING) || TABLE_COL_PAGE_COUNT_DEFAULT
      );
      state.tableColumns.columnInfo.paginationCount =
        tableColumnsInitialState.columnInfo.paginationCount;
      state.tableColumns.columnInfo.page = tableColumnsInitialState.columnInfo.page;
      state.tableColumns.searchStr = tableColumnsInitialState.searchStr;
      state.tableColumns.columnInfo.searchCount = tableColumnsInitialState.columnInfo.searchCount;
    }
  },

  setErrorLoadingColumns: (
    state: IDataSourceState,
    action: PayloadAction<{
      status: boolean;
      errorCode: number;
    }>
  ) => {
    state.tableColumns.isLoading = false;
    state.tableColumns.hasError = action.payload.status;
    state.tableColumns.errorCode = action.payload.errorCode;
  },

  setColumns: (
    state: IDataSourceState,
    action: PayloadAction<{
      data: ITableColumnData[];
      searchCount: number;
      selectedCount: number;
      totalCount: number;
    }>
  ) => {
    state.tableColumns.isLoading = false;
    state.tableColumns.columnInfo.data = action.payload.data;
    state.tableColumns.columnInfo.searchCount = action.payload.searchCount;
    state.tableColumns.columnInfo.selectedCount = action.payload.selectedCount;
    state.tableColumns.columnInfo.totalCount = action.payload.totalCount;
    state.tableColumns.columnList = action.payload.data;
    state.activeDataSourceSelectedColCount = action.payload.selectedCount;
    state.tableColumns.columnInfo.paginationCount = calculatePaginationCount(
      action.payload.totalCount,
      action.payload.searchCount,
      state.tableColumns.searchStr,
      state.tableColumns.columnInfo.colCountPerPage
    );
  },

  setColumnsSearchStr: (state: IDataSourceState, action: PayloadAction<{ searchStr: string }>) => {
    state.tableColumns.searchStr = action.payload.searchStr;
    state.tableColumns.columnInfo.page = 1;
  },

  updateColumnsPerPage: (
    state: IDataSourceState,
    action: PayloadAction<{ colCountPerPage: number }>
  ) => {
    const paginationCount = calculatePaginationCount(
      state.tableColumns.columnInfo.totalCount,
      state.tableColumns.columnInfo.searchCount,
      state.tableColumns.searchStr,
      action.payload.colCountPerPage
    );

    state.tableColumns.columnInfo.page = 1;
    state.tableColumns.columnInfo.colCountPerPage = action.payload.colCountPerPage;
    state.tableColumns.columnInfo.paginationCount = paginationCount;

    // also set the col count per page to local storage
    localStorage.setItem(TABLE_COL_COUNT_LOCAL_SETTING, String(action.payload.colCountPerPage));
  },

  updatePage: (state: IDataSourceState, action: PayloadAction<{ page: number }>) => {
    state.tableColumns.columnInfo.page = action.payload.page;
  },

  resetPreviousPath: (state: IDataSourceState) => {
    state.previousPath = studioInitialPath;
  },

  setPreviousPath: (state: IDataSourceState, action: PayloadAction<string>) => {
    state.previousPath = action.payload;
  },

  // delete data source modal
  resetDeleteDataSourceState: (state: IDataSourceState) => {
    state.deleteDataSourceProps.isConfirmOpen = initialDeleteDataSourceProps.isConfirmOpen;
    state.deleteDataSourceProps.isDeleting = initialDeleteDataSourceProps.isDeleting;
    state.deleteDataSourceProps.hasDeleteError = initialDeleteDataSourceProps.hasDeleteError;
    state.deleteDataSourceProps.errorMsg = initialDeleteDataSourceProps.errorMsg;
  },

  openDeleteDataSourceConfirmationModal: (state: IDataSourceState) => {
    state.deleteDataSourceProps.isConfirmOpen = true;
    state.deleteDataSourceProps.isDeleting = false;
    state.deleteDataSourceProps.hasDeleteError = false;
    state.deleteDataSourceProps.errorMsg = '';
  },

  closeDeleteDataSourceConfirmationModal: (state: IDataSourceState) => {
    state.deleteDataSourceProps.isConfirmOpen = false;
    state.deleteDataSourceProps.isDeleting = false;
    state.deleteDataSourceProps.hasDeleteError = false;
    state.deleteDataSourceProps.errorMsg = '';
  },

  settRequestingDeleteDataSource: (state: IDataSourceState) => {
    state.deleteDataSourceProps.isDeleting = true;
    state.deleteDataSourceProps.hasDeleteError = false;
    state.deleteDataSourceProps.errorMsg = '';
    state.deleteDataSourceProps.isConfirmOpen = false;
  },

  setDeleteDataSourceSuccess: (state: IDataSourceState) => {
    state.deleteDataSourceProps.isDeleting = false;
    state.deleteDataSourceProps.isConfirmOpen = false;
    state.deleteDataSourceProps.hasDeleteError = false;
    state.deleteDataSourceProps.errorMsg = '';
  },

  setDeleteDataSourceError: (
    state: IDataSourceState,
    action: PayloadAction<{ errorMsg: string }>
  ) => {
    state.deleteDataSourceProps.isDeleting = false;
    state.deleteDataSourceProps.hasDeleteError = true;
    state.deleteDataSourceProps.errorMsg = action.payload.errorMsg;
  },
};

const DatasourcesReduxSlice = createSlice({
  name: 'datasources',
  initialState: datasourcesInitialState,
  reducers: updateDatasourcesReduxSlice,
});

export const {
  // table columns
  initLoadingColumns,
  setColumns,
  setErrorLoadingColumns,
  updateColumnsPerPage,
  updatePage,
  setColumnsSearchStr,

  // datasources
  setIsLoadingDatasources,
  setDataSources,
  setDataSourceTypes,
  filterDataSourcesByType,
  filterDataSourcesByName,
  setIsDataSourceTypesError,
  setErrorLoadingDataSources,
  setIsDataSourceTypesLoading,

  // delete data source modal
  resetDeleteDataSourceState,
  openDeleteDataSourceConfirmationModal,
  closeDeleteDataSourceConfirmationModal,
  settRequestingDeleteDataSource,
  setDeleteDataSourceSuccess,
  setDeleteDataSourceError,

  // route
  setPreviousPath,
  resetPreviousPath,

  // tables
  setTablesIsLoading,
  setDataSourceTables,
  setErrorLoadingTables,
  setActiveDataSourceTable,
  updateDataSourceTablesPaginationPageLimit,
  updateDataSourceTablesPaginationSearchTerm,
  updateDataSourceTablesPaginationCurrentPage,
  resetDataSourceTablesPagination,
  setActiveDataSourceTableColumnCounts,
} = DatasourcesReduxSlice.actions;

export default DatasourcesReduxSlice.reducer;
